import React, { useCallback } from "react";
import { TextField } from "@material-ui/core";

function getSelectionRange(input, fallbackPosition) {
  const selectionStart =
    typeof input.selectionStart === "number"
      ? input.selectionStart
      : fallbackPosition;
  const selectionEnd =
    typeof input.selectionEnd === "number" ? input.selectionEnd : selectionStart;

  return { selectionStart, selectionEnd };
}

export default function MultilineTextField({
  value,
  onValueChange,
  onChange,
  onKeyDown,
  supportsTabs = true,
  tabString = "\t",
  rows = 6,
  inputProps = {},
  ...props
}) {
  const currentValue = value == null ? "" : String(value);

  const notifyValueChange = useCallback(
    (nextValue, event) => {
      if (onValueChange) {
        onValueChange(nextValue);
        return;
      }

      if (onChange) {
        onChange({
          ...event,
          target: {
            ...event.target,
            value: nextValue,
          },
          currentTarget: {
            ...event.currentTarget,
            value: nextValue,
          },
        });
      }
    },
    [onChange, onValueChange],
  );

  const handleChange = useCallback(
    (event) => {
      notifyValueChange(event.target.value, event);
    },
    [notifyValueChange],
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (onKeyDown) {
        onKeyDown(event);
      }

      if (event.defaultPrevented || !supportsTabs || event.key !== "Tab") {
        return;
      }

      event.preventDefault();

      const input = event.target;
      const { selectionStart, selectionEnd } = getSelectionRange(
        input,
        currentValue.length,
      );
      const nextValue = `${currentValue.slice(
        0,
        selectionStart,
      )}${tabString}${currentValue.slice(selectionEnd)}`;
      const nextCursorPosition = selectionStart + tabString.length;

      notifyValueChange(nextValue, event);

      if (typeof window !== "undefined" && window.requestAnimationFrame) {
        window.requestAnimationFrame(() => {
          input.selectionStart = nextCursorPosition;
          input.selectionEnd = nextCursorPosition;
        });
      }
    },
    [currentValue, notifyValueChange, onKeyDown, supportsTabs, tabString],
  );

  return (
    <TextField
      {...props}
      value={currentValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      multiline
      fullWidth
      rows={rows}
      inputProps={{
        ...inputProps,
        style: {
          whiteSpace: "pre-wrap",
          tabSize: 4,
          ...(inputProps.style || {}),
        },
      }}
    />
  );
}
